import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { RouteMap } from '@/routes/Routes';
import useAlertStore from '@/stores/alertStore';
import { VERIFY_AND_CHANGE_EMAIL, RESET_PASSWORD, RECOVER_EMAIL } from '@/constants/actions';
import { useUpdateEmail } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { auth } from '@/firebase/firebase';
import { errorReporter } from '@/services/errorReportingService';

const FIREBASE_AUTH_DOMAIN = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const FIREBASE_AUTH_URL = `https://${FIREBASE_AUTH_DOMAIN}/__/auth/action`;

const EmailAction = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlertStore();
  const { currentUser, fetchAndSetUserData } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode') || sessionStorage.getItem('emailActionMode');
  const oobCode = params.get('oobCode') || sessionStorage.getItem('emailActionOobCode');

  const { mutateAsync: updateEmail } = useUpdateEmail();

  useEffect(() => {
    if (mode && oobCode) {
      sessionStorage.setItem('emailActionMode', mode);
      sessionStorage.setItem('emailActionOobCode', oobCode);
    }

    if (!mode || !oobCode) {
      navigate(RouteMap.HOME);
      return;
    }

    const waitForUser = async (maxRetries = 10, delay = 500) => {
      let retries = 0;
      while (retries < maxRetries) {
        const updatedUser = useAuthStore.getState().currentUser;
        if (updatedUser) return updatedUser;

        await new Promise((resolve) => setTimeout(resolve, delay));
        await fetchAndSetUserData({ force: true });
        retries++;
      }
      throw new Error('No authenticated user found after multiple retries.');
    };

    const handleVerifyAndChangeEmail = async () => {
      try {
        setIsProcessing(true);
        const updatedUser = await waitForUser();

        const newEmail = localStorage.getItem('pendingNewEmail');
        if (!newEmail) throw new Error('New email not found in storage.');

        await updateEmail({
          headers: { authorization: `Bearer ${await updatedUser.getIdToken()}` },
          body: { email: newEmail },
        });

        await applyActionCode(auth, oobCode);
        await updatedUser.reload();

        localStorage.removeItem('pendingNewEmail');
        showAlert('Your email has been updated successfully!', 'success');
        navigate(RouteMap.HOME);
      } catch (error) {
        errorReporter.expected('Failed to process Firebase email action', error, {
          feature: 'auth',
          operation: 'emailAction',
        });
        if (!isProcessing) {
          navigate(RouteMap.HOME);
        }
      } finally {
        sessionStorage.removeItem('emailActionMode');
        sessionStorage.removeItem('emailActionOobCode');
        setIsProcessing(false);
      }
    };

    const actionHandlers: Record<string, () => Promise<void>> = {
      [VERIFY_AND_CHANGE_EMAIL]: handleVerifyAndChangeEmail,
      [RESET_PASSWORD]: async () => {
        window.location.href = `${FIREBASE_AUTH_URL}?mode=${mode}&oobCode=${oobCode}`;
      },
      [RECOVER_EMAIL]: async () => {
        window.location.href = `${FIREBASE_AUTH_URL}?mode=${mode}&oobCode=${oobCode}`;
      },
    };

    const handleAction = async () => {
      const actionHandler = actionHandlers[mode!];

      if (actionHandler) {
        await actionHandler();
      } else {
        showAlert('Unknown action type.', 'error');
        navigate(RouteMap.HOME);
      }
    };

    handleAction();
  }, [mode, oobCode, currentUser, fetchAndSetUserData, isProcessing, navigate, showAlert, updateEmail]);

  return null;
};

export default EmailAction;
